import { useState, useEffect, useCallback } from 'react';
import { useIntersection } from '@farmersdog/corgi-x';
import type { DotLottie } from '@lottiefiles/dotlottie-react';
import { useHasScrolled } from '../useHasScrolled';

export interface UseLottieAnimationProps {
  /** Visibility threshold to trigger the animation (0 to 1). Default: 1 */
  threshold?: number;
  /** How much of the element must be visible to trigger (0 = any, 1 = fully visible) */
  rootMargin?: string;
  /**
   * If true, the animation will only play once the element becomes visible.
   * If false, it will play every time the element enters the viewport.
   */
  playOnce?: boolean;
}

export interface UseLottieAnimationReturn {
  /** Ref callback for the container element to observe visibility */
  containerRef: (node: HTMLElement | null) => void;
  /** Ref callback to store the Lottie player instance */
  lottieRefCallback: (instance: DotLottie | null) => void;
  /** Whether the animation has already played */
  hasPlayed: boolean;
  /** Whether the user has scrolled on the page */
  hasScrolled: boolean;
}

export function useLottieAnimation({
  threshold = 1,
  rootMargin = '0px 0px -200px 0px',
  playOnce = true,
}: UseLottieAnimationProps = {}): UseLottieAnimationReturn {
  const [hasPlayed, setHasPlayed] = useState(false);
  const [player, setPlayer] = useState<DotLottie | null>(null);
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);

  const hasScrolled = useHasScrolled();
  const intersection = useIntersection(containerEl, { threshold, rootMargin });

  useEffect(() => {
    if (!player || !hasScrolled || !intersection?.isIntersecting) {
      return;
    }

    if (playOnce) {
      if (!hasPlayed) {
        player.play();
        setHasPlayed(true);
      }
    } else {
      player.play();
    }
  }, [hasScrolled, intersection?.isIntersecting, player, hasPlayed, playOnce]);

  const containerRef = useCallback((node: HTMLElement | null) => {
    setContainerEl(node);
  }, []);

  const lottieRefCallback = useCallback((instance: DotLottie | null) => {
    setPlayer(instance);
  }, []);

  return {
    containerRef,
    lottieRefCallback,
    hasPlayed,
    hasScrolled,
  };
}
